import { useState, useEffect, useRef } from 'react';

export type HeaderTheme = 'light' | 'dark';

export function useHeaderTheme() {
  const [currentTheme, setCurrentTheme] = useState<HeaderTheme>('light');
  const [isScrolled, setIsScrolled] = useState(false);
  const sectionsRef = useRef<Map<string, HeaderTheme>>(new Map());

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      // SIMPLE: Just check which sections have 'dark' theme and if we're over them
      const darkSections = document.querySelectorAll('[data-section-theme="dark"]');
      let isDarkSection = false;

      for (const section of darkSections) {
        const rect = section.getBoundingClientRect();
        // If header area (top 80px) overlaps with this dark section
        if (rect.top < 80 && rect.bottom > 0) {
          isDarkSection = true;
          break;
        }
      }

      const newTheme = isDarkSection ? 'dark' : 'light';
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
        console.log(`[HeaderTheme] Switched to ${newTheme} theme`);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentTheme]);

  // Function to register a section with its theme
  const registerSection = (element: HTMLElement, sectionId: string, theme: HeaderTheme) => {
    if (!element) return;

    // Just add the data attribute so scroll handler can find it
    element.setAttribute('data-section-theme', theme);
    element.setAttribute('data-section-id', sectionId);
    sectionsRef.current.set(sectionId, theme);
  };

  // Function to unregister a section
  const unregisterSection = (element: HTMLElement, sectionId: string) => {
    if (!element) return;

    element.removeAttribute('data-section-theme');
    element.removeAttribute('data-section-id');
    sectionsRef.current.delete(sectionId);
  };

  return {
    currentTheme,
    isScrolled,
    registerSection,
    unregisterSection,
  };
}
